import {
    DASHBOARD_DEFAULT_MAP_CENTER,
    DASHBOARD_DEFAULT_MAP_ZOOM,
} from "@/core/constants/dashboard.constants";
import type {
    DashboardCacheVersions,
    DashboardMapConfig,
    DashboardPermissions,
    DashboardScope,
    DashboardScopeType,
} from "@/core/interfaces/Dashboard";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Module, Mutation, VuexModule } from "vuex-module-decorators";

export interface DashboardFullscreenState {
  widget: string | null;
}

@Module({
  namespaced: true,
  name: "DashboardModule",
})
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default class DashboardModule extends VuexModule {
  scope: DashboardScope | null = null;
  selectedMarkerId: number | null = null;
  versions: DashboardCacheVersions = {
    global: 0,
    map: 0,
  };
  permissions: DashboardPermissions = {
    canViewAllUnits: false,
    canFilterUnit: false,
    canFilterWarehouse: false,
    canViewFinancialValues: false,
    canViewAlerts: true,
  };
  mapConfig: DashboardMapConfig = {
    countryCode: "VN",
    center: { ...DASHBOARD_DEFAULT_MAP_CENTER },
    zoom: DASHBOARD_DEFAULT_MAP_ZOOM,
    minZoom: 5,
    maxZoom: 16,
  };
  capDonVi: number | null = null;
  metaLoaded = false;
  fullscreen: DashboardFullscreenState = { widget: null };
  movementGroupBy: "day" | "week" | "month" = "day";
  movementMetric: "amount" | "quantity" = "amount";

  get currentScope(): DashboardScope | null {
    return this.scope;
  }

  get currentVersions(): DashboardCacheVersions {
    return this.versions;
  }

  get currentPermissions(): DashboardPermissions {
    return this.permissions;
  }

  get isMetaLoaded(): boolean {
    return this.metaLoaded;
  }

  get fullscreenWidget(): string | null {
    return this.fullscreen.widget;
  }

  @Mutation
  setScope(scope: DashboardScope): void {
    this.scope = { ...scope };
  }

  @Mutation
  patchScope(partial: Partial<DashboardScope>): void {
    if (!this.scope) {
      return;
    }
    this.scope = { ...this.scope, ...partial };
  }

  @Mutation
  setScopeType(type: DashboardScopeType): void {
    if (!this.scope) {
      return;
    }
    this.scope = { ...this.scope, type };
  }

  @Mutation
  setDateRange(payload: { fromDate: string; toDate: string }): void {
    if (!this.scope) {
      return;
    }
    this.scope = {
      ...this.scope,
      fromDate: payload.fromDate,
      toDate: payload.toDate,
    };
  }

  @Mutation
  setSelectedMarkerId(id: number | null): void {
    this.selectedMarkerId = id;
  }

  @Mutation
  setVersions(versions: DashboardCacheVersions): void {
    this.versions = { ...this.versions, ...versions };
  }

  @Mutation
  setPermissions(permissions: DashboardPermissions): void {
    this.permissions = { ...permissions };
  }

  @Mutation
  setMapConfig(config: DashboardMapConfig): void {
    this.mapConfig = { ...config };
  }

  @Mutation
  setCapDonVi(cap: number): void {
    this.capDonVi = cap;
  }

  @Mutation
  setMetaLoaded(loaded: boolean): void {
    this.metaLoaded = loaded;
  }

  @Mutation
  setFullscreenWidget(widget: string | null): void {
    this.fullscreen = { widget };
  }

  @Mutation
  setMovementOptions(payload: {
    groupBy?: "day" | "week" | "month";
    metric?: "amount" | "quantity";
  }): void {
    if (payload.groupBy) {
      this.movementGroupBy = payload.groupBy;
    }
    if (payload.metric) {
      this.movementMetric = payload.metric;
    }
  }

  @Mutation
  resetDashboardUi(): void {
    this.selectedMarkerId = null;
    this.fullscreen = { widget: null };
  }

  /** Full reset when auth user / tenant context changes. */
  @Mutation
  resetDashboardState(): void {
    this.scope = null;
    this.selectedMarkerId = null;
    this.versions = {
      global: 0,
      map: 0,
    };
    this.permissions = {
      canViewAllUnits: false,
      canFilterUnit: false,
      canFilterWarehouse: false,
      canViewFinancialValues: false,
      canViewAlerts: true,
    };
    this.mapConfig = {
      countryCode: "VN",
      center: { ...DASHBOARD_DEFAULT_MAP_CENTER },
      zoom: DASHBOARD_DEFAULT_MAP_ZOOM,
      minZoom: 5,
      maxZoom: 16,
    };
    this.capDonVi = null;
    this.metaLoaded = false;
    this.fullscreen = { widget: null };
    this.movementGroupBy = "day";
    this.movementMetric = "amount";
  }
}
